import { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
    Brain, Save, Loader2, Zap, CheckCircle2, XCircle, 
    Eye, EyeOff, Star, Activity, Shield, Key
} from 'lucide-react';
import { toast } from 'sonner';

const AdminAISettingsPage = () => {
    const { api, isSuperAdmin } = useAdmin();
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingProvider, setSavingProvider] = useState(null);
    const [testingProvider, setTestingProvider] = useState(null);
    const [editingProvider, setEditingProvider] = useState(null);
    const [showApiKey, setShowApiKey] = useState({});
    const [formData, setFormData] = useState({});

    useEffect(() => {
        fetchProviders();
    }, [api]);

    const fetchProviders = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/ai-providers');
            setProviders(response.data || []);
        } catch (error) {
            console.error('Failed to fetch AI providers:', error);
            toast.error('Failed to load AI providers');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleProvider = async (provider) => {
        if (!isSuperAdmin) {
            toast.error('Only super admins can modify providers');
            return;
        }

        try {
            const response = await api.put(`/admin/ai-providers/${provider}/toggle`);
            setProviders(prev => prev.map(p => 
                p.provider === provider ? { ...p, is_enabled: response.data.is_enabled } : p
            ));
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to toggle provider');
        }
    };

    const handleSetDefault = async (provider) => {
        if (!isSuperAdmin) {
            toast.error('Only super admins can set default provider');
            return;
        }

        try {
            await api.put(`/admin/ai-providers/${provider}/set-default`);
            setProviders(prev => prev.map(p => ({
                ...p,
                is_default: p.provider === provider
            })));
            toast.success(`${provider} set as default system model`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to set default provider');
        }
    };

    const handleTestProvider = async (provider) => {
        setTestingProvider(provider);
        try {
            const response = await api.post(`/admin/ai-providers/${provider}/test`);
            if (response.data.success) {
                toast.success(`${provider}: ${response.data.message}`);
            } else {
                toast.error(`${provider}: ${response.data.message}`);
            }
        } catch (error) {
            toast.error(`Test failed: ${error.response?.data?.detail || error.message}`);
        } finally {
            setTestingProvider(null);
        }
    };

    const handleStartEdit = (provider) => {
        const providerData = providers.find(p => p.provider === provider);
        setFormData({
            [provider]: {
                api_key: '',
                analysis_model: providerData?.analysis_model || '',
                chat_model: providerData?.chat_model || ''
            }
        });
        setEditingProvider(provider);
    };

    const handleSaveProvider = async (provider) => {
        if (!isSuperAdmin) {
            toast.error('Only super admins can update providers');
            return;
        }

        const data = formData[provider];
        if (!data) return;

        // Build update payload
        const updatePayload = {};
        if (data.api_key) updatePayload.api_key = data.api_key;
        if (data.analysis_model) updatePayload.analysis_model = data.analysis_model;
        if (data.chat_model) updatePayload.chat_model = data.chat_model;

        if (Object.keys(updatePayload).length === 0) {
            toast.info('No changes to save');
            return;
        }

        setSavingProvider(provider);
        try {
            await api.put(`/admin/ai-providers/${provider}`, updatePayload);
            toast.success(`${provider} configuration updated`);
            setEditingProvider(null);
            setFormData({});
            fetchProviders();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update provider');
        } finally {
            setSavingProvider(null);
        }
    };

    const getProviderIcon = (provider) => {
        const icons = {
            emergent: '🚀',
            openai: '🤖',
            anthropic: '🧠',
            google: '🔮'
        };
        return icons[provider] || '⚡';
    };

    const getProviderColor = (provider) => {
        const colors = {
            emergent: 'from-teal-500 to-emerald-500',
            openai: 'from-green-500 to-teal-500',
            anthropic: 'from-orange-500 to-amber-500',
            google: 'from-blue-500 to-indigo-500'
        };
        return colors[provider] || 'from-slate-500 to-slate-600';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-ai-settings-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Brain className="w-7 h-7 text-teal-400" />
                    AI Configuration
                </h1>
                <p className="text-slate-400 mt-1">Configure AI providers and models for lab report analysis</p>
            </div>

            {!isSuperAdmin && (
                <Card className="border-amber-500/50 bg-amber-500/10">
                    <CardContent className="p-4 flex items-center gap-3">
                        <Shield className="w-5 h-5 text-amber-400" />
                        <p className="text-amber-200 text-sm">
                            Only super admins can modify AI settings. You have read-only access.
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* AI Providers */}
            <Card className="border-slate-700 bg-slate-800/50">
                <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Zap className="w-5 h-5 text-teal-400" />
                        AI Providers
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                        Select and configure AI providers. The default provider handles all AI requests.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {providers.map((provider) => (
                        <div 
                            key={provider.provider}
                            className={`border rounded-lg p-4 transition-all ${
                                provider.is_default 
                                    ? 'border-teal-500/50 bg-teal-900/20' 
                                    : provider.is_enabled 
                                        ? 'border-slate-600 bg-slate-700/30' 
                                        : 'border-slate-700 bg-slate-800/30 opacity-60'
                            }`}
                            data-testid={`provider-card-${provider.provider}`}
                        >
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                {/* Provider Info */}
                                <div className="flex items-center gap-4 flex-1">
                                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${getProviderColor(provider.provider)} flex items-center justify-center text-2xl`}>
                                        {getProviderIcon(provider.provider)}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-semibold text-white">{provider.display_name}</h3>
                                            {provider.is_default && (
                                                <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/30">
                                                    <Star className="w-3 h-3 mr-1 fill-current" /> Default
                                                </Badge>
                                            )}
                                            {provider.is_enabled ? (
                                                <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                                                    <CheckCircle2 className="w-3 h-3 mr-1" /> Enabled
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
                                                    <XCircle className="w-3 h-3 mr-1" /> Disabled
                                                </Badge>
                                            )}
                                            {provider.has_api_key && (
                                                <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                                                    <Key className="w-3 h-3 mr-1" /> Key Set
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-slate-400 mt-1">
                                            Analysis: <span className="text-slate-300">{provider.analysis_model}</span> | 
                                            Chat: <span className="text-slate-300">{provider.chat_model}</span>
                                        </p>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    {/* Enable/Disable Toggle */}
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-400">Enable</span>
                                        <Switch
                                            checked={provider.is_enabled}
                                            onCheckedChange={() => handleToggleProvider(provider.provider)}
                                            disabled={!isSuperAdmin}
                                        />
                                    </div>
                                    
                                    {/* Set Default Button */}
                                    {provider.is_enabled && !provider.is_default && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleSetDefault(provider.provider)}
                                            disabled={!isSuperAdmin}
                                            className="border-teal-500/50 text-teal-300 hover:bg-teal-500/20"
                                        >
                                            <Star className="w-3 h-3 mr-1" /> Set Default
                                        </Button>
                                    )}
                                    
                                    {/* Test Button */}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleTestProvider(provider.provider)}
                                        disabled={testingProvider === provider.provider || !provider.is_enabled}
                                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                    >
                                        {testingProvider === provider.provider ? (
                                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        ) : (
                                            <Activity className="w-3 h-3 mr-1" />
                                        )}
                                        Test
                                    </Button>
                                    
                                    {/* Configure Button */}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => editingProvider === provider.provider ? setEditingProvider(null) : handleStartEdit(provider.provider)}
                                        disabled={!isSuperAdmin}
                                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                    >
                                        {editingProvider === provider.provider ? 'Cancel' : 'Configure'}
                                    </Button>
                                </div>
                            </div>

                            {/* Configuration Form */}
                            {editingProvider === provider.provider && (
                                <div className="mt-4 pt-4 border-t border-slate-700 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* API Key (not for emergent) */}
                                        {provider.provider !== 'emergent' && (
                                            <div className="space-y-2">
                                                <Label className="text-slate-300">API Key</Label>
                                                <div className="flex gap-2">
                                                    <Input
                                                        type={showApiKey[provider.provider] ? 'text' : 'password'}
                                                        value={formData[provider.provider]?.api_key || ''}
                                                        onChange={(e) => setFormData(prev => ({
                                                            ...prev,
                                                            [provider.provider]: {
                                                                ...prev[provider.provider],
                                                                api_key: e.target.value
                                                            }
                                                        }))}
                                                        placeholder={provider.has_api_key ? '••••••••' : 'Enter API key'}
                                                        className="bg-slate-700/50 border-slate-600 text-white"
                                                    />
                                                    <Button
                                                        size="icon"
                                                        variant="outline"
                                                        onClick={() => setShowApiKey(prev => ({ ...prev, [provider.provider]: !prev[provider.provider] }))}
                                                        className="border-slate-600"
                                                    >
                                                        {showApiKey[provider.provider] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    </Button>
                                                </div>
                                                <p className="text-xs text-slate-500">Stored securely on server</p>
                                            </div>
                                        )}

                                        {/* Analysis Model */}
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Analysis Model Name</Label>
                                            <Input
                                                value={formData[provider.provider]?.analysis_model || ''}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    [provider.provider]: {
                                                        ...prev[provider.provider],
                                                        analysis_model: e.target.value
                                                    }
                                                }))}
                                                placeholder={provider.analysis_model}
                                                className="bg-slate-700/50 border-slate-600 text-white"
                                            />
                                            <p className="text-xs text-slate-500">e.g., gpt-4o, gemini-2.5-flash</p>
                                        </div>

                                        {/* Chat Model */}
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Chat Model Name</Label>
                                            <Input
                                                value={formData[provider.provider]?.chat_model || ''}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    [provider.provider]: {
                                                        ...prev[provider.provider],
                                                        chat_model: e.target.value
                                                    }
                                                }))}
                                                placeholder={provider.chat_model}
                                                className="bg-slate-700/50 border-slate-600 text-white"
                                            />
                                            <p className="text-xs text-slate-500">e.g., gpt-5.2, claude-sonnet-4.5</p>
                                        </div>
                                    </div>

                                    {provider.provider === 'emergent' && (
                                        <div className="bg-teal-900/20 rounded-lg p-3 border border-teal-500/30">
                                            <p className="text-sm text-teal-300">
                                                <strong>Emergent AI</strong> uses the Universal Key from environment. No API key needed.
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex justify-end">
                                        <Button
                                            onClick={() => handleSaveProvider(provider.provider)}
                                            disabled={savingProvider === provider.provider}
                                            className="bg-teal-500 hover:bg-teal-600"
                                        >
                                            {savingProvider === provider.provider ? (
                                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                                            ) : (
                                                <><Save className="w-4 h-4 mr-2" /> Save Configuration</>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="border-slate-700 bg-slate-800/50">
                <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">How it works</h3>
                    <div className="space-y-3 text-sm text-slate-400">
                        <p>• <strong className="text-slate-300">Default Provider:</strong> All AI requests (report analysis & chat) are routed through the default provider.</p>
                        <p>• <strong className="text-slate-300">API Keys:</strong> For OpenAI, Google, or Anthropic, you need to provide your own API key.</p>
                        <p>• <strong className="text-slate-300">Model Names:</strong> Specify exact model names (e.g., gpt-4o, gemini-2.5-flash, claude-sonnet-4.5).</p>
                        <p>• <strong className="text-slate-300">Testing:</strong> Use the Test button to verify your configuration works before setting as default.</p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminAISettingsPage;
